import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import apiService from "../services/apiService";
import { Plus } from "lucide-react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EditCharacters = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [character, setCharacter] = useState({
    description: "",
    name: "",
    content_id: "",
    title: "",
    character_img: "",
  });

  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  let searchTimeout = null;

  useEffect(() => {
    fetchCharacter();
  }, []);

  const fetchCharacter = async () => {
    try {
      const response = await apiService.fetchData(`admin/character/${id}`);
      if (!response.error && response.data) {
        setCharacter({
          description: response.data.character_desc || "",
          name: response.data.character_name || "",
          content_id: response.data.content_id || "",
          title: response.data.CharactersToContentDetails?.title || "",
          character_img: response.data.character_img || "",
        });
      }
    } catch (error) {
      console.error("Error fetching character:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCharacter({ ...character, [name]: value });

    if (name === "title") {
      if (searchTimeout) clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => handleSearch(value), 300);
    }
  };

  const handleSearch = async (query) => {
    if (!query) {
      setSearchResults([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await apiService.postData("admin/search-content", { title: query });
      const data = response.data || [];
      setSearchResults(Array.isArray(data) ? data : []);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error fetching search results:", error);
      setSearchResults([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectTitle = (title, content_id) => {
    setCharacter({ ...character, title, content_id });
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("character_desc", character.description);
    formData.append("character_name", character.name);
    formData.append("content_id", character.content_id);
    formData.append("title", character.title);

    try {
      const response = await apiService.putData(`admin/character/${id}`, formData);
      if (!response.error) {
        toast.success("Character updated successfully!");
        navigate("/characters");
      }
    } catch (error) {
      toast.error("Error updating character.");
    }
  };

  const deleteImage = async () => {
    try {
      const res = await apiService.deleteData(`admin/image/character/${id}`);
      if (!res.error) {
        toast.success("Image deleted!");
        fetchCharacter();
      } else {
        toast.error("Failed to delete image.");
      }
    } catch (err) {
      toast.error("Error deleting image.");
    }
  };

  const uploadImage = async () => {
    if (!selectedImage) return toast.error("Please select a file.");
    setIsUploading(true);
    const formData = new FormData();
    formData.append("id", id);
    formData.append("import_type", "character");
    formData.append("files", selectedImage);
    try {
      const res = await apiService.postData("admin/upload-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!res.error) {
        toast.success("Image uploaded!");
        setShowModal(false);
        fetchCharacter();
      } else toast.error("File upload failed.");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="row">
      <div className="col-12 col-md-12">
        <div className="card">
          <div className="card-header">
            <h4>Edit Character</h4>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>

              <div className="mb-3 form-group">
                <label>Character Preview</label>
                {character.character_img ? (
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <img src={apiService.getFullImageUrl(character.character_img)} alt="Character" style={{ maxWidth: "200px", borderRadius: "8px" }} />
                    <span
                      style={{
                        position: "absolute",
                        top: "-8px",
                        right: "-8px",
                        backgroundColor: "#dc3545",
                        color: "#fff",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer"
                      }}
                      onClick={deleteImage}
                    >×</span>
                  </div>
                ) : (
                  <p>No image uploaded yet.</p>
                )}
                <div className="mt-2">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setShowModal(true)}>
                    {character.character_img ? "Update Image" : "Upload Image"}
                  </button>
                </div>
              </div>

              <div className="form-group position-relative">
                <label>Select Content</label>
                <input
                  type="text"
                  name="title"
                  className="form-control"
                  value={character.title}
                  onChange={handleChange}
                  placeholder="Search Content"
                  autoComplete="off"
                />
                {showSuggestions && searchResults.length > 0 && (
                  <div className="list-group position-absolute w-100 mt-1" style={{ zIndex: 10 }}>
                    {searchResults.map((item) => (
                      <button
                        type="button"
                        className="list-group-item list-group-item-action"
                        key={item.content_id}
                        onClick={() => handleSelectTitle(item.title, item.content_id)}
                      >
                        {item.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group mt-3">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={character.name}
                  onChange={handleChange}
                  placeholder="Enter Name"
                />
              </div>

              <div className="form-group mt-3">
                <label>Description</label>
                <textarea
                  name="description"
                  className="form-control"
                  value={character.description}
                  onChange={handleChange}
                  placeholder="Enter Description"
                ></textarea>
              </div>

              <div className="d-flex gap-2 mt-3">
                <button type="submit" className="btn btn-primary"><Plus size={16} /> Update Character</button>
                <button type="button" className="btn btn-secondary" onClick={() => navigate("/characters")}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Upload New Image</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <input type="file" accept="image/*" onChange={(e) => {
                  const file = e.target.files[0];
                  setSelectedImage(file);
                  if (file) setPreviewImage(URL.createObjectURL(file));
                }} className="form-control" />
                {previewImage && <img src={previewImage} alt="Preview" className="mt-3" style={{ maxWidth: "100%", borderRadius: "8px" }} />}
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={uploadImage} disabled={isUploading}>
                  {isUploading ? "Uploading..." : "Save"}
                </button>
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCharacters;
